import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, from, EMPTY } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { StorageService } from '../services/storage.service';
import { LoginService } from '../services/login.service';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../environments/environment';

const TOKEN_KEY = environment.tokenName;

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  token: string;

  // LoginService depends on HttpClient, so it is resolved lazily to avoid a DI cycle
  constructor(
    private storageService: StorageService,
    private router: Router,
    private injector: Injector,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    console.log('In intercept: ');

    return from(this.storageService.get(TOKEN_KEY)).pipe(
      switchMap((token) => {
        if (token?.length > 0) {
          console.log('Setting jwtToken: ' + token);
          this.storageService.driver();
          request = request.clone({
            headers: request.headers.set('Authorization', 'Bearer ' + token),
          });
        }

        if (Capacitor.isNativePlatform()) {
          console.log('Setting mobile app flag');
          request = request.clone({ headers: request.headers.set('X-IsMobileApp', 'true') });
        }

        if (!request.headers.has('Content-Type')) {
          request = request.clone({
            headers: request.headers.set('Content-Type', 'application/json'),
          });
        }

        if (!request.headers.has('Accept')) {
          request = request.clone({ headers: request.headers.set('Accept', 'application/json') });
        }

        return next.handle(request).pipe(
          map((event: HttpEvent<any>) => {
            if (event instanceof HttpResponse) {
              console.log('event--->>>', event);
            }
            return event;
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401 && !this.isLoginRequest(request)) {
              console.log('HTTP ERROR RESPONSE: 401 session expired');
              return from(this.handleSessionExpired()).pipe(switchMap(() => EMPTY));
            }

            const message = this.getErrorMessage(error);
            console.log('HTTP ERROR RESPONSE: ' + error.status + ' ' + message);
            return throwError(
              () =>
                new HttpErrorResponse({
                  error: message,
                  headers: error.headers,
                  status: error.status,
                  statusText: error.statusText,
                  url: error.url ?? undefined,
                }),
            );
          }),
        );
      }),
    );
  }

  private isLoginRequest(request: HttpRequest<any>): boolean {
    return /\/login$/i.test(request.url.split('?')[0]);
  }

  // The API reports errors as a plain string body, { error } or { Message }
  private getErrorMessage(error: HttpErrorResponse): string {
    const body = error.error;
    const candidates = [body, body?.error, body?.Message];
    return candidates.find((c) => typeof c === 'string' && c.length > 0) ?? error.message;
  }

  // Same outcome as the login guards: close the tab when launched from ACP, otherwise go to login
  private async handleSessionExpired(): Promise<void> {
    const isRoutedFromACP = await this.storageService.get('isRoutedFromACP');
    await this.injector.get(LoginService).logout();
    if (isRoutedFromACP === true) {
      window.close();
    } else {
      this.router.navigateByUrl('/login', { replaceUrl: true });
    }
  }
}
